import React, { useState } from "react";
import ImageUploader from "./ImageUploader";
import WaitingModal from "./WaitingModal";
import { APPLIANCE_TYPE_OPTIONS } from "../../constants/applianceTypes";
import logger from "../../services/logger";
import UploadServiceInstance from "../../services/upload.service";
import SearchServiceInstance from "../../services/search.service";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 23, 44, 0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
  padding: "16px",
};

const modalStyle = {
  background: "#ffffff",
  borderRadius: "16px",
  padding: "20px",
  maxWidth: "640px",
  width: "100%",
  position: "relative",
  maxHeight: "85vh",
  overflowY: "auto",
};

const closeButtonStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  border: "none",
  background: "transparent",
  fontSize: "22px",
  cursor: "pointer",
  color: "#64748b",
};

const ApplianceScannerModal = ({
  isOpen = false,
  onClose = () => {},
  onImageUpload = () => {},
  onConfirm = () => {},
  onPopinConfirm = () => {},
}) => {
  const [pict, setPict] = useState(null);
  const [pictUrl, setPictUrl] = useState("");
  const [precision, setPrecision] = useState("");
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [manualFields, setManualFields] = useState({
    type: "",
    marque: "",
    modele: "",
  });
  const [uploaderKey, setUploaderKey] = useState(0);

  const handleImageUpload = (uploadedUrl, uploadedImage) => {
    setPict(uploadedImage);
    setPictUrl(uploadedUrl || "");
    onImageUpload(uploadedUrl, uploadedImage);
    setResult(null);
    setErrorMessage("");
  };

  const handleSearchClick = async () => {
    if (!pict) {
      setErrorMessage("Ajoute une photo pour lancer la recherche.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    setResult(null);
    try {
      const uploadResult = await UploadServiceInstance.uploadTempFile(pict);
      const url = uploadResult ? uploadResult.fileUrl : "";
      const data = await SearchServiceInstance.searchApplianceByImage(
        url,
        precision
      );
      const nextResult = data || null;
      setResult(nextResult);
      if (nextResult) {
        setManualFields({
          type: nextResult.type || "",
          marque: nextResult.marque || "",
          modele: nextResult.modele || "",
        });
      }
    } catch (error) {
      logger.log("Erreur recherche appareil :", error);
      setErrorMessage("Une erreur est survenue lors de la recherche.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!result) return;
    const payload = {
      type: manualFields.type || result?.type || "",
      marque: manualFields.marque || result?.marque || "",
      modele: manualFields.modele || result?.modele || "",
      pict: pictUrl || "",
    };
    onConfirm({
      ...payload,
      model: payload.modele || "",
    });
    onPopinConfirm(payload);
    onClose();
  };

  const handleManualChange = (field) => (event) => {
    const value = event.target.value;
    setManualFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRetake = () => {
    setPict(null);
    setPictUrl("");
    setResult(null);
    setErrorMessage("");
    setManualFields({ type: "", marque: "", modele: "" });
    setUploaderKey((prev) => prev + 1);
    onImageUpload(null, null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={overlayStyle} onClick={onClose}>
        <div
          style={modalStyle}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="Fermer"
          >
            ×
          </button>
          <ImageUploader
            key={uploaderKey}
            onImageUpload={handleImageUpload}
          />
          <div style={{ marginTop: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px" }}>
              Précision (optionnel)
            </label>
            <input
              value={precision}
              onChange={(event) => setPrecision(event.target.value)}
              placeholder="ex: lave-linge, frigo..."
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5f5",
              }}
            />
          </div>
          <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={handleSearchClick}
              style={{
                border: "none",
                borderRadius: "999px",
                background: "var(--color-green)",
                color: "#fff",
                padding: "10px 18px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {isLoading ? "Recherche..." : "Lancer recherche"}
            </button>
          </div>
          {errorMessage && (
            <div style={{ marginTop: "10px", color: "#b91c1c" }}>
              {errorMessage}
            </div>
          )}
          {result && (
            <>
              <div style={{ marginTop: "12px" }}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ display: "block", marginBottom: "6px" }}>
                  Type
                </label>
                <select
                  value={manualFields.type}
                  onChange={handleManualChange("type")}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5f5",
                  }}
                >
                  <option value="">Sélectionner un type</option>
                  {APPLIANCE_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ display: "block", marginBottom: "6px" }}>
                  Marque
                </label>
                <input
                  value={manualFields.marque}
                  onChange={handleManualChange("marque")}
                  placeholder="ex: Bosch"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5f5",
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "6px" }}>
                  Modèle
                </label>
                <input
                  value={manualFields.modele}
                  onChange={handleManualChange("modele")}
                  placeholder="ex: WAU28"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5f5",
                  }}
                />
              </div>
            </div>
              <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  style={{
                    border: "none",
                    borderRadius: "999px",
                    background: "var(--color-green)",
                    color: "#fff",
                    padding: "10px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Confirmer
                </button>
                <button
                  type="button"
                  onClick={handleRetake}
                  disabled={isLoading}
                  style={{
                    border: "1px solid #94a3b8",
                    borderRadius: "999px",
                    background: "#fff",
                    color: "#475569",
                    padding: "10px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Reprendre la photo
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {isLoading && <WaitingModal />}
    </>
  );
};

export default ApplianceScannerModal;
